import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

/**
 * Per-user secret presence map. All keys are namespaced (`{NAME}__{uid}`)
 * server-side; this map exposes presence-only booleans keyed by the short
 * secret name.
 */
export interface SecretStatus {
  apiKeys: Record<string, boolean>;
  provider: { twilioAuthToken: boolean; telnyxApiKey: boolean };
  /** LiveKit add-on credentials. Optional because older deployments
   *  may still return a payload without it (graceful degradation). */
  livekit?: {
    apiKey: boolean;
    apiSecret: boolean;
    sipUsername: boolean;
    sipPassword: boolean;
  };
}

export interface ApiKeysInput {
  openaiKey?: string;
  anthropicKey?: string;
  googleAiKey?: string;
  groqKey?: string;
  mistralKey?: string;
  openrouterKey?: string;
  xaiKey?: string;
  elevenlabsKey?: string;
  deepgramKey?: string;
}

export function useSecretStatus() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<SecretStatus>({
    queryKey: ['secret-status', targetUid] as const,
    enabled: !!user && !!targetUid,
    staleTime: 30_000,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string }, SecretStatus>(functions, 'adminListSecretStatus');
        const r = await fn({ uid: targetUid });
        return r.data;
      }
      const fn = httpsCallable<unknown, SecretStatus>(functions, 'listSecretStatus');
      const r = await fn({});
      return r.data;
    },
  });
}

export function useUpdateApiKeys() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (input: ApiKeysInput) => {
      const payload = adminMode && targetUid ? { ...input, uid: targetUid } : input;
      const fn = httpsCallable<ApiKeysInput & { uid?: string }, { ok: true; written: string[] }>(
        functions,
        'updateApiKeys',
      );
      const r = await fn(payload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['secret-status', targetUid] }),
  });
}
